import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "../loading";

// Thin wrappers around nhsuk-frontend components. The markup and icons are copied from the component
// templates in node_modules/nhsuk-frontend/packages/components, so these render exactly as the NHS
// design system intends - keep them in step if the package is upgraded.

export const HelpLoading = () => (
  <div style={{ margin: "4rem auto", textAlign: "center" }}>
    <LoadingSpinner />
  </div>
);

const TickIcon = () => (
  <svg
    className="nhsuk-icon nhsuk-icon__tick"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    width="34"
    height="34"
  >
    <path strokeWidth="4" strokeLinecap="round" d="M18.4 7.8l-8.5 8.4L5.6 12" stroke="#007f3b" />
  </svg>
);

const CrossIcon = () => (
  <svg
    className="nhsuk-icon nhsuk-icon__cross"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    width="34"
    height="34"
  >
    <path
      d="M17 18.5c-.4 0-.8-.1-1.1-.4l-10-10c-.6-.6-.6-1.6 0-2.1.6-.6 1.5-.6 2.1 0l10 10c.6.6.6 1.5 0 2.1-.3.3-.6.4-1 .4z"
      fill="#d5281b"
    />
    <path
      d="M7 18.5c-.4 0-.8-.1-1.1-.4-.6-.6-.6-1.5 0-2.1l10-10c.6-.6 1.5-.6 2.1 0 .6.6.6 1.5 0 2.1l-10 10c-.3.3-.6.4-1 .4z"
      fill="#d5281b"
    />
  </svg>
);

// The standard icon marking a link that leaves the platform. Sits inline with link or button text.
export const ExternalIcon = () => (
  <svg
    className="rs-help-external-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M14 4h6v6" />
    <path d="M20 4l-8 8" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);

// A link out of the portal. Flags the new window both visually and to screen readers.
export const ExternalLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer noopener">
    {children}
    <ExternalIcon />
    <span className="nhsuk-u-visually-hidden"> (opens in a new window)</span>
  </a>
);

// nhsuk-do-dont-list. Items are nodes so a point can carry its own sub-list.
const MarkList = ({
  title,
  items,
  type,
}: {
  title: string;
  items: ReactNode[];
  type: "tick" | "cross";
}) => (
  <div className="nhsuk-do-dont-list">
    <h2 className="nhsuk-do-dont-list__label">{title}</h2>
    <ul className={`nhsuk-list nhsuk-list--${type}`} role="list">
      {items.map((item, index) => (
        <li key={index}>
          {type === "tick" ? <TickIcon /> : <CrossIcon />}
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export const TickList = (props: { title: string; items: ReactNode[] }) => (
  <MarkList {...props} type="tick" />
);

export const CrossList = (props: { title: string; items: ReactNode[] }) => (
  <MarkList {...props} type="cross" />
);

// nhsuk-card-group. Children must be HelpCards - the group is a list.
export const HelpCardGroup = ({ children }: { children: ReactNode }) => (
  <ul className="nhsuk-grid-row nhsuk-card-group">{children}</ul>
);

// nhsuk-card, clickable so the whole card is the hit area. The coloured icon is a decorative
// extension of the standard component. externalUrl wins over to.
export const HelpCard = ({
  to,
  externalUrl,
  title,
  summary,
  accent,
  icon,
}: {
  to?: string;
  externalUrl?: string;
  title: string;
  summary: string;
  accent?: string;
  icon?: ReactNode;
}) => (
  <li className="nhsuk-grid-column-one-half nhsuk-card-group__item">
    <div className="nhsuk-card nhsuk-card--clickable">
      <div className="nhsuk-card__content">
        {icon && (
          <span className="rs-help-card__icon" style={{ backgroundColor: accent }}>
            {icon}
          </span>
        )}
        <h2 className="nhsuk-card__heading nhsuk-heading-s">
          {externalUrl ? (
            <a
              className="nhsuk-card__link"
              href={externalUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {title}
              <ExternalIcon />
              <span className="nhsuk-u-visually-hidden"> (opens in a new window)</span>
            </a>
          ) : (
            <Link className="nhsuk-card__link" to={to!}>
              {title}
            </Link>
          )}
        </h2>
        <p className="nhsuk-card__description">{summary}</p>
      </div>
    </div>
  </li>
);

// nhsuk-back-link.
export const HelpBackLink = ({ to, text }: { to: string; text: string }) => (
  <div className="nhsuk-back-link">
    <Link className="nhsuk-back-link__link" to={to}>
      <svg
        className="nhsuk-icon nhsuk-icon__chevron-left"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
        height="24"
        width="24"
      >
        <path d="M8.5 12c0-.3.1-.5.3-.7l5-5c.4-.4 1-.4 1.4 0s.4 1 0 1.4L10.9 12l4.3 4.3c.4.4.4 1 0 1.4s-1 .4-1.4 0l-5-5c-.2-.2-.3-.4-.3-.7z" />
      </svg>
      {text}
    </Link>
  </div>
);

// nhsuk-pagination - the NHS previous/next page pattern.
export const HelpPagination = ({
  previous,
  next,
}: {
  previous?: { to: string; title: string };
  next?: { to: string; title: string };
}) => {
  if (!previous && !next) return null;

  return (
    <nav className="nhsuk-pagination" role="navigation" aria-label="Pagination">
      <ul className="nhsuk-list nhsuk-pagination__list">
        {previous && (
          <li className="nhsuk-pagination-item--previous">
            <Link
              className="nhsuk-pagination__link nhsuk-pagination__link--prev"
              to={previous.to}
            >
              <span className="nhsuk-pagination__title">Previous</span>
              <span className="nhsuk-u-visually-hidden">:</span>
              <span className="nhsuk-pagination__page">{previous.title}</span>
              <svg
                className="nhsuk-icon nhsuk-icon__arrow-left"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                width="34"
                height="34"
              >
                <path d="M4.1 12.3l2.7 3c.2.2.5.2.7 0 .1-.1.1-.2.1-.3v-2h11c.6 0 1-.4 1-1s-.4-1-1-1h-11V9c0-.2-.1-.4-.3-.5h-.2c-.1 0-.3.1-.4.2l-2.7 3c0 .2 0 .4.1.6z" />
              </svg>
            </Link>
          </li>
        )}
        {next && (
          <li className="nhsuk-pagination-item--next">
            <Link className="nhsuk-pagination__link nhsuk-pagination__link--next" to={next.to}>
              <span className="nhsuk-pagination__title">Next</span>
              <span className="nhsuk-u-visually-hidden">:</span>
              <span className="nhsuk-pagination__page">{next.title}</span>
              <svg
                className="nhsuk-icon nhsuk-icon__arrow-right"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                width="34"
                height="34"
              >
                <path d="M19.6 11.66l-2.73-3A.51.51 0 0 0 16 9v2H5a1 1 0 0 0 0 2h11v2a.5.5 0 0 0 .32.46.39.39 0 0 0 .18 0 .52.52 0 0 0 .37-.16l2.73-3a.5.5 0 0 0 0-.64z" />
              </svg>
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
};

// nhsuk-expander - a question that opens to reveal its answer. Native details/summary, no JS.
export const HelpExpander = ({ question, children }: { question: string; children: ReactNode }) => (
  <details className="nhsuk-details nhsuk-expander">
    <summary className="nhsuk-details__summary">
      <span className="nhsuk-details__summary-text">{question}</span>
    </summary>
    <div className="nhsuk-details__text">{children}</div>
  </details>
);

export const HelpExpanderGroup = ({ children }: { children: ReactNode }) => (
  <div className="nhsuk-expander-group">{children}</div>
);
